import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette } from '../ui';
import { useCommandSearch, type CommandResult } from '../../lib/search/useCommandSearch';
import { OPEN_SEARCH_EVENT } from '../../lib/searchEvents';

// Global Cmd+K search (story 1E-1, D42). Owns the palette's open/query state,
// listens for the ⌘K / Ctrl+K shortcut (and a custom event the TopBar button
// fires), and routes on select. The palette + result sourcing are reusable
// pieces (CommandPalette primitive + useCommandSearch); this only wires them
// into the shell.
// NOT re-exported (story 2-147wg, same reasoning as `AgentPanel.tsx`'s own
// event constants): the canonical definition now lives in
// `lib/searchEvents.ts`, a lower-layer, JSX-free home `TopBar.tsx`
// (layout-primitive) imports from directly — re-exporting an imported
// binding here would trip `react-refresh/only-export-components` (its
// `allowConstantExport` carve-out only recognizes a direct `export const`
// literal declaration).

export function GlobalSearch(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { items, loading } = useCommandSearch(query);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpen);
    };
  }, []);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const onSelect = (item: CommandResult) => {
    close();
    navigate(item.to);
  };

  return (
    <CommandPalette
      open={open}
      onClose={close}
      query={query}
      onQueryChange={setQuery}
      items={items}
      loading={loading}
      onSelect={onSelect}
      placeholder="Search navigation, settings, members…"
    />
  );
}
